import { SafeHydrate } from "@/SafeHydrate";
import { ArrowLeft, Briefcase, Calendar, CheckCircle2, Clock, Code2, DollarSign, Globe, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const projectData = {
  title: "¿Sientes que has llegado a un techo en tu desarrollo profesional?",
  postedAt: "Enfoque Integral",
  location: "Sesiones Online",
  description: ["Este proceso está diseñado específicamente para profesionales y líderes que sienten que su carrera ha perdido el brillo inicial y buscan reencontrar su propósito vital. Si te despiertas sintiendo que tu trabajo es una carga en lugar de una plataforma de crecimiento, este espacio es para ti.", "Para aquellos que experimentan un agotamiento constante, falta de equilibrio entre su vida personal y laboral, o si simplemente sienten que 'falta algo' a pesar de haber alcanzado el éxito externo. Trabajaremos para reconectar con tus valores fundamentales y alinear tu carrera con lo que realmente importa.", "Acompaño a emprendedores y directivos que desean transitar de una gestión basada en la urgencia y el estrés hacia un liderazgo consciente, auténtico y con un impacto real en sus equipos y en su propia calidad de vida."],
  skills: ["Propósito", "Mindfulness", "Liderazgo", "Autoconocimiento", "Equilibrio", "Resiliencia"],
  deliverables: ["Identificación de bloqueos y creencias limitantes", "Definición de una visión de carrera a largo plazo", "Herramientas prácticas de gestión emocional diaria", "Plan de acción personalizado para tu transformación"],
  budget: "Inversión en ti",
  deadline: "12 Sesiones",
  projectType: "Personalizado",
  experienceLevel: "Senior",
  client: {
    name: "Guía Certificada",
    rating: 5.0,
    reviews: 48,
    verified: true
  }
};
export default function ProjectOverview() {
  return <section data-section-id="147661" id="para-quién-es-este-proceso" className="bg-background py-16">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className="mb-8">
          <Link to="/CoachingSessionsPage" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-accent transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Sesiones
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {}
          <div className="lg:col-span-8 space-y-10">
            {}
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight text-foreground mb-4">
                {projectData.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground font-sans">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {projectData.postedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {projectData.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Globe className="w-4 h-4" />
                  Global
                </span>
              </div>
            </div>

            {}
            <div className="space-y-6">
              <h2 className="text-2xl font-display font-semibold text-foreground">
                ¿Para quién es este proceso?
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed font-sans text-base">
                {projectData.description.map((paragraph, i) => <p key={i} data-index={i}>
                    {paragraph}
                  </p>)}
              </div>
            </div>

            {}
            <div className="space-y-6">
              <h2 className="text-xl font-display font-semibold text-foreground flex items-center gap-2">
                <Code2 className="w-5 h-5 text-accent" />
                Pilares del Proceso
              </h2>
              <div className="flex flex-wrap gap-2">
                {projectData.skills.map((skill, i) => <span key={i} data-index={i} className="inline-flex items-center px-3 py-1 rounded-md bg-muted-light-background text-muted-foreground text-sm font-medium border border-muted">
                    {skill}
                  </span>)}
              </div>
            </div>

            {}
            <div className="space-y-6">
              <h2 className="text-xl font-display font-semibold text-foreground">
                Resultados Esperados
              </h2>
              <ul className="space-y-3 font-sans">
                {projectData.deliverables.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div className="lg:col-span-4 sticky top-24">
            <Card className="bg-background border-muted shadow-soft">
              <CardHeader className="pb-4 border-b border-muted">
                <CardTitle className="text-lg font-display">Resumen del Programa</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="p-2 rounded-full bg-muted-light-background text-accent">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <span className="font-sans font-medium">Inversión</span>
                  </div>
                  <span className="font-sans font-semibold text-foreground">
                    {projectData.budget}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="p-2 rounded-full bg-muted-light-background text-accent">
                      <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
                    </div>
                    <span className="font-sans font-medium">Duración</span>
                  </div>
                  <span className="font-sans font-semibold text-foreground">
                    {projectData.deadline}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="p-2 rounded-full bg-muted-light-background text-accent">
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <span className="font-sans font-medium">Modalidad</span>
                  </div>
                  <span className="font-sans font-semibold text-foreground">
                    {projectData.projectType}
                  </span>
                </div>

                <div className="pt-4 border-t border-muted">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground font-sans">Acompañamiento</span>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-foreground font-sans">
                        {projectData.client.name}
                      </span>
                      {projectData.client.verified && <CheckCircle2 className="w-4 h-4 text-primary" />}
                    </div>
                  </div>
                  <div className="flex items-center justify-between font-sans">
                    <span className="text-sm text-muted-foreground">Satisfacción</span>
                    <span className="text-sm font-medium text-foreground">
                       {projectData.client.rating} ({projectData.client.reviews}{" "}
                      testimonios)
                    </span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="pt-2 pb-6 px-6">
                <Button size="lg" className="w-full text-base font-semibold bg-primary hover:bg-primary-hover text-foreground-primary shadow-soft">
                  Reservar Sesión
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}